"use client";

import { motion } from "framer-motion";
import type { CSSProperties } from "react";

import { cn } from "@/lib/utils";

const COLUMN_COUNT = 2;
const ROW_COUNT = 4;
const CYCLE_DURATION_SECONDS = 2.4;
const DIM_OPACITY = 0.1;
const BRIGHT_OPACITY = 0.85;

const BRAILLE_STATES: readonly (readonly boolean[])[] = [
  [true, false, true, false, false, true, false, true],
  [false, true, false, true, true, false, true, false],
  [true, true, false, false, false, false, true, true],
  [false, false, true, true, true, true, false, false],
  [true, false, false, true, false, true, true, false],
  [false, true, true, false, true, false, false, true],
] as const;

const BRAILLE_DOT_POSITION_CLASSES = [
  "ml-[-3px] mt-[-6.25px]",
  "ml-[-3px] mt-[-2.75px]",
  "ml-[-3px] mt-[0.75px]",
  "ml-[-3px] mt-[4.25px]",
  "ml-[1px] mt-[-6.25px]",
  "ml-[1px] mt-[-2.75px]",
  "ml-[1px] mt-[0.75px]",
  "ml-[1px] mt-[4.25px]",
] as const;

function opacityKeyframes(dotIndex: number): number[] {
  return BRAILLE_STATES.map((state) =>
    state[dotIndex] ? BRIGHT_OPACITY : DIM_OPACITY,
  );
}

export function BrailleLoadingIndicator({
  className,
  color = "currentColor",
  prefersReducedMotion = false,
}: {
  className?: string;
  color?: string;
  prefersReducedMotion?: boolean;
}) {
  return (
    <span
      aria-hidden="true"
      className={cn("relative inline-block size-5 shrink-0", className)}
    >
      {Array.from({ length: COLUMN_COUNT }, (_, column) =>
        Array.from({ length: ROW_COUNT }, (_, row) => {
          const dotIndex = column * ROW_COUNT + row;
          const keyframes = opacityKeyframes(dotIndex);

          return (
            <motion.span
              key={`${column}-${row}`}
              className={cn(
                "absolute top-1/2 left-1/2 size-[2px] rounded-full bg-[color:var(--braille-dot-color)]",
                BRAILLE_DOT_POSITION_CLASSES[dotIndex],
              )}
              style={{ "--braille-dot-color": color } as CSSProperties}
              initial={false}
              animate={{
                opacity: prefersReducedMotion ? BRIGHT_OPACITY : keyframes,
              }}
              transition={
                prefersReducedMotion
                  ? { duration: 0 }
                  : {
                      duration: CYCLE_DURATION_SECONDS,
                      ease: "easeInOut",
                      repeat: Infinity,
                    }
              }
            />
          );
        }),
      )}
    </span>
  );
}
